import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import useEmblaCarousel from 'embla-carousel-react';
import { useState, useCallback, useMemo, useEffect, } from 'react';
import styleUtils from '../../styles/styleUtils';
import { usePointerState } from '../../utils/usePointerState';
import { FlatPager } from '../FlatPager';
import { ImageUnavailable } from '../ImageUnavailable';
import { ResponsiveImage } from '../ResponsiveImage/ResponsiveImage';
const carouselContainerStyles = css `
  overflow: hidden;
`;
const flatPagerContainerStyles = css `
  align-self: flex-end;
  display: flex;
  flex-direction: row;
  position: absolute;
  width: 100%;
`;
const imageStyles = css `
  aspect-ratio: 1/1;
  height: auto;
  width: 100%;
`;
const slideListStyles = css `
  ${styleUtils.listReset}
  aspect-ratio: 1/1;
  display: flex;
  height: 100%;
  width: 100%;
`;
const slideListItemStyles = css `
  flex: 0 0 100%;
  min-width: 0;
`;
const getImageLoadingState = ({ imageLoading, index, }) => ((index === 0
    ? (imageLoading === 'eagerLazy'
        ? 'eager'
        : imageLoading)
    : 'lazy'));
const ImageCycler = ({ imageLoading = 'eagerLazy', images, isFlatPagerVisible, onSelectedImageIndexChange, selectedImageIndex = 0, }) => {
    const pointerState = usePointerState();
    const isTouchPointer = useMemo(() => (pointerState === 'TOUCH'), [
        pointerState,
    ]);
    const [emblaOptions, setEmblaOptions,] = useState({
        loop: true,
        startIndex: 0,
        watchDrag: isTouchPointer,
    });
    const [emblaRef, emblaApi,] = useEmblaCarousel(emblaOptions);
    const [isHovering, setIsHovering,] = useState(false);
    const flatPager = useMemo(() => ((isFlatPagerVisible
        && images.length > 1)
        ? (_jsx("div", Object.assign({ css: flatPagerContainerStyles }, { children: images
                .map((image, index) => (_jsx(FlatPager, { isSelected: index === selectedImageIndex, modifier: "dark" }, `flatPager-${(image === null || image === void 0 ? void 0 : image.id) ? (image === null || image === void 0 ? void 0 : image.id) + '-' : ''}${index}`))) })))
        : undefined), [
        images,
        isFlatPagerVisible,
        selectedImageIndex,
    ]);
    const onMouseEnter = useCallback(() => {
        setIsHovering(true);
    }, []);
    const onMouseLeave = useCallback(() => {
        setIsHovering(false);
    }, []);
    const onImageChange = useCallback((emblaApi) => {
        onSelectedImageIndexChange(emblaApi.selectedScrollSnap());
    }, [
        onSelectedImageIndexChange,
    ]);
    useEffect(() => {
        if (isTouchPointer) {
            setEmblaOptions({
                watchDrag: true,
            });
        }
        else {
            setEmblaOptions({
                watchDrag: false,
            });
        }
    }, [
        isTouchPointer,
    ]);
    useEffect(() => {
        if (isTouchPointer
            && emblaApi) {
            onImageChange(emblaApi);
            emblaApi
                .on('reInit', onImageChange)
                .on('select', onImageChange);
        }
    }, [
        emblaApi,
        isTouchPointer,
        onImageChange,
    ]);
    useEffect(() => {
        let interval;
        if (isHovering) {
            interval = setInterval(() => {
                if (emblaApi) {
                    emblaApi.scrollNext();
                    onImageChange(emblaApi);
                }
            }, 1500);
        }
        return () => {
            clearInterval(interval);
            onSelectedImageIndexChange(0);
            if (emblaApi) {
                emblaApi.scrollTo(0);
            }
        };
    }, [
        emblaApi,
        isHovering,
        onImageChange,
        onSelectedImageIndexChange,
    ]);
    return (_jsxs("div", Object.assign({ css: carouselContainerStyles, ref: emblaRef }, { children: [_jsx("ul", Object.assign({ css: slideListStyles, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave }, { children: images
                    .map((image, index) => (_jsx("li", Object.assign({ css: slideListItemStyles }, { children: (image === null || image === void 0 ? void 0 : image.src)
                        ? (_jsx(ResponsiveImage, { alt: image === null || image === void 0 ? void 0 : image.alt, css: imageStyles, fallbackComponent: _jsx(ImageUnavailable, {}), imageLoading: getImageLoadingState({
                                imageLoading,
                                index,
                            }), imageSources: [
                                {
                                    src: image === null || image === void 0 ? void 0 : image.src,
                                },
                            ]
                                .concat((image === null || image === void 0 ? void 0 : image.srcAlt)
                                ? [
                                    {
                                        src: image.srcAlt,
                                    },
                                ]
                                : [])
                                .flat() }))
                        : (_jsx(ImageUnavailable, {})) }), `${(image === null || image === void 0 ? void 0 : image.id) ? (image === null || image === void 0 ? void 0 : image.id) + '-' : ''}${index + 1}`))) })), flatPager] })));
};
export { ImageCycler };
